import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { listTiers } from '../../services/tierBranchApi';
import { getTierAccounts, assignTierAccount, unassignTierAccount } from '../../services/bankingApi';
import type { Tier } from '../../types/tierBranch';
import type { TierAccounts } from '../../types/banking';

/* Assign bank accounts to a tier's pool. A branch then picks from its tier's
   pool (in the branch portal), and deposits route to those accounts. */
export default function TierPoolsTab() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [tierId, setTierId] = useState<number | ''>('');
  const [pool, setPool] = useState<TierAccounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [addId, setAddId] = useState<number | ''>('');

  useEffect(() => {
    listTiers().then((t) => {
      setTiers(t);
      if (t.length) setTierId(Number(t[0].id));
    });
  }, []);

  const load = async (id: number) => {
    setLoading(true);
    try { setPool(await getTierAccounts(id)); } finally { setLoading(false); }
  };
  useEffect(() => { if (tierId) load(Number(tierId)); }, [tierId]);

  const add = async () => {
    if (!tierId || !addId) return;
    await assignTierAccount(Number(tierId), Number(addId));
    setAddId(''); await load(Number(tierId));
  };
  const remove = async (accountId: number) => {
    if (!tierId) return;
    await unassignTierAccount(Number(tierId), accountId); await load(Number(tierId));
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <TextField select label="Tier" size="small" sx={{ minWidth: 220 }}
          value={tierId} onChange={(e) => setTierId(Number(e.target.value))}>
          {tiers.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
        {pool && (
          <>
            <TextField select label="Add account to tier" size="small" sx={{ minWidth: 260 }}
              value={addId} onChange={(e) => setAddId(Number(e.target.value))}>
              {pool.available.length
                ? pool.available.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.label || a.method} · {a.method} {a.upiId || a.accountNumber}
                    </MenuItem>
                  ))
                : <MenuItem disabled value="">No unassigned accounts</MenuItem>}
            </TextField>
            <Button variant="contained" onClick={add} disabled={!addId}>Assign</Button>
          </>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
      ) : pool ? (
        <Stack spacing={1.5}>
          {pool.assigned.length ? pool.assigned.map((a) => (
            <Paper key={a.id} variant="outlined"
              sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip size="small" label={a.method} />
                <Chip size="small" variant="outlined" label={a.mode} />
                <Typography variant="body2">{a.label || '-'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {a.method === 'UPI' ? a.upiId : `${a.bankName} ${a.accountNumber}`}
                </Typography>
              </Stack>
              <Button size="small" color="error" startIcon={<X size={14} />} onClick={() => remove(a.id)}>
                Remove
              </Button>
            </Paper>
          )) : (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No accounts assigned to this tier yet.
            </Typography>
          )}
        </Stack>
      ) : null}
    </Box>
  );
}
